// src/pages/SamplesPage.js
import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  FaSearch,
  FaFilter,
  FaFilePdf,
  FaDownload,
  FaEye,
  FaStar,
  FaGraduationCap,
  FaBook,
  FaClock,
  FaUserGraduate,
  FaTag,
  FaTh,
  FaList,
  FaSortAmountDown,
  FaSortAmountUp,
  FaTimes,
  FaChevronRight,
  FaChevronLeft,
  FaExternalLinkAlt
} from 'react-icons/fa';
import './SamplesPage.css';

// Popular tags/categories like Edusson
const POPULAR_CATEGORIES = [
  'Business', 'Finance', 'Economics', 'Marketing', 'Management',
  'Education', 'Higher Education', 'College', 'Learning',
  'History', 'American History', 'World History', 'Civil War',
  'Literature', 'Book Review', 'Literary Analysis', 'Poetry',
  'Science', 'Technology', 'Research', 'Innovation',
  'Health', 'Medicine', 'Nursing', 'Public Health',
  'Law', 'Ethics', 'Justice', 'Legal Studies',
  'Psychology', 'Sociology', 'Anthropology', 'Social Sciences',
  'Philosophy', 'Critical Thinking', 'Ethics',
  'Art', 'Design', 'Creativity', 'Music',
  'Environment', 'Climate Change', 'Sustainability',
  'Politics', 'Government', 'International Relations'
];

const PAPER_TYPES = [
  'Essay', 'Research Paper', 'Thesis', 'Dissertation', 'Assignment',
  'Term Paper', 'Book Report', 'Book Review', 'Speech', 'Personal Statement',
  'Literature Review', 'Article Review', 'Coursework', 'Lab Report', 'Homework',
  'Case Study', 'Capstone Project', 'Proposal', 'Reflection Paper',
  'Annotated Bibliography', 'Article', 'Summary', 'Critique'
];

const ACADEMIC_LEVELS = [
  'High School', 'College', 'Undergraduate', 'Bachelor',
  'Master', 'PhD', 'Doctoral', 'Professional'
];

const FORMAT_STYLES = ['APA', 'MLA', 'Chicago', 'Harvard', 'IEEE', 'Other'];

const SamplesPage = () => {
  const navigate = useNavigate();
  const searchInputRef = useRef(null);
  
  const [samples, setSamples] = useState([]);
  const [filteredSamples, setFilteredSamples] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // Search and filter states
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedPaperType, setSelectedPaperType] = useState('');
  const [selectedAcademicLevel, setSelectedAcademicLevel] = useState('');
  const [selectedFormat, setSelectedFormat] = useState('');
  
  // View states
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
  const [sortBy, setSortBy] = useState('newest'); // 'newest', 'oldest', 'popular', 'alphabetical'
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(12);
  
  // Featured samples
  const [featuredSamples, setFeaturedSamples] = useState([]);
  
  // Modal for sample preview
  const [selectedSample, setSelectedSample] = useState(null);
  const [showPreviewModal, setShowPreviewModal] = useState(false);

  useEffect(() => {
    fetchSamples();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [samples, searchQuery, selectedCategory, selectedPaperType, selectedAcademicLevel, selectedFormat, sortBy]);

  const fetchSamples = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/samples/samples/');
      const data = response.data.results || response.data;
      setSamples(data);
      
      // Get featured samples
      const featured = data.filter(sample => sample.is_featured).slice(0, 6);
      setFeaturedSamples(featured);
      
    } catch (err) {
      console.error('Error fetching samples:', err);
      setError('Failed to load samples. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...samples];

    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(sample =>
        sample.title?.toLowerCase().includes(query) ||
        sample.subject?.toLowerCase().includes(query) ||
        sample.abstract?.toLowerCase().includes(query) ||
        sample.keywords?.toLowerCase().includes(query)
      );
    }

    // Category filter (using tags)
    if (selectedCategory) {
      filtered = filtered.filter(sample =>
        sample.tags?.some(tag => tag.name === selectedCategory) ||
        sample.category?.name === selectedCategory ||
        sample.subject === selectedCategory
      );
    }

    // Paper type filter
    if (selectedPaperType) {
      filtered = filtered.filter(sample =>
        sample.paper_type === selectedPaperType.toUpperCase()
      );
    }

    // Academic level filter
    if (selectedAcademicLevel) {
      filtered = filtered.filter(sample =>
        sample.academic_level === selectedAcademicLevel.toUpperCase()
      );
    }

    // Format filter
    if (selectedFormat) {
      filtered = filtered.filter(sample =>
        sample.format_style === selectedFormat
      );
    }

    // Apply sorting
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'newest':
          return new Date(b.published_at || b.created_at) - new Date(a.published_at || a.created_at);
        case 'oldest':
          return new Date(a.published_at || a.created_at) - new Date(b.published_at || b.created_at);
        case 'popular':
          return (b.views || 0) - (a.views || 0);
        case 'alphabetical':
          return a.title.localeCompare(b.title);
        default:
          return 0;
      }
    });

    setFilteredSamples(filtered);
    setCurrentPage(1); // Reset to first page when filters change
  };

  const clearFilters = () => {
    setSearchQuery('');
    setSelectedCategory('');
    setSelectedPaperType('');
    setSelectedAcademicLevel('');
    setSelectedFormat('');
    if (searchInputRef.current) {
      searchInputRef.current.value = '';
    }
  };

  const handleCategoryClick = (category) => {
    setSelectedCategory(category === selectedCategory ? '' : category);
  };

  const handleSampleClick = (sample) => {
    if (sample.slug) {
      navigate(`/samples/${sample.slug}`);
    } else {
      navigate(`/samples/${sample.id}`);
    }
  };

  const handlePreviewClick = (sample, e) => {
    e.stopPropagation();
    setSelectedSample(sample);
    setShowPreviewModal(true);
  };

  const handleDownload = async (sample, e) => {
    e.stopPropagation();
    try {
      const response = await axios.post(`/api/samples/samples/${sample.id}/download/`);
      if (response.data.download_url) {
        window.open(response.data.download_url, '_blank');
      }
    } catch (err) {
      console.error('Download error:', err);
      // Show download modal or redirect to login if needed
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getReadingTime = (wordCount) => {
    const wordsPerMinute = 200;
    const minutes = Math.ceil(wordCount / wordsPerMinute);
    return `${minutes} min read`;
  };

  // Pagination calculations
  const totalPages = Math.ceil(filteredSamples.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentSamples = filteredSamples.slice(startIndex, endIndex);

  const goToPage = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const renderPagination = () => {
    if (totalPages <= 1) return null;

    const pages = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <button
          key={i}
          onClick={() => goToPage(i)}
          className={`pagination-btn ${currentPage === i ? 'active' : ''}`}
        >
          {i}
        </button>
      );
    }

    return (
      <div className="pagination">
        <button
          onClick={() => goToPage(currentPage - 1)}
          disabled={currentPage === 1}
          className="pagination-nav"
        >
          <FaChevronLeft /> Previous
        </button>
        
        <div className="pagination-numbers">
          {startPage > 1 && (
            <>
              <button onClick={() => goToPage(1)} className="pagination-btn">1</button>
              {startPage > 2 && <span className="pagination-dots">...</span>}
            </>
          )}
          {pages}
          {endPage < totalPages && (
            <>
              {endPage < totalPages - 1 && <span className="pagination-dots">...</span>}
              <button onClick={() => goToPage(totalPages)} className="pagination-btn">{totalPages}</button>
            </>
          )}
        </div>
        
        <button
          onClick={() => goToPage(currentPage + 1)}
          disabled={currentPage === totalPages}
          className="pagination-nav"
        >
          Next <FaChevronRight />
        </button>
      </div>
    );
  };

  const renderSampleCard = (sample) => (
    <div key={sample.id} className="sample-card" onClick={() => handleSampleClick(sample)}>
      <div className="sample-card-header">
        {sample.thumbnail_url ? (
          <img src={sample.thumbnail_url} alt={sample.title} className="sample-thumbnail" />
        ) : (
          <div className="sample-thumbnail-placeholder">
            <FaFilePdf className="placeholder-icon" />
          </div>
        )}
        
        <div className="sample-badges">
          <span className="badge-level">{sample.academic_level?.replace('_', ' ')}</span>
          <span className="badge-type">{sample.paper_type?.replace('_', ' ')}</span>
          {sample.is_featured && (
            <span className="badge-featured">
              <FaStar /> Featured
            </span>
          )}
        </div>
      </div>
      
      <div className="sample-card-body">
        <h3 className="sample-title">{sample.title}</h3>
        
        <div className="sample-meta">
          <span className="meta-item">
            <FaGraduationCap /> {sample.subject}
          </span>
          <span className="meta-item">
            <FaBook /> {sample.format_style}
          </span>
          {sample.word_count && (
            <span className="meta-item">
              <FaClock /> {getReadingTime(sample.word_count)}
            </span>
          )}
        </div>
        
        <p className="sample-excerpt">{sample.excerpt || sample.abstract?.substring(0, 150) + '...'}</p>
        
        <div className="sample-tags">
          {sample.tags?.slice(0, 3).map(tag => (
            <span key={tag.id} className="sample-tag" onClick={(e) => {
              e.stopPropagation();
              handleCategoryClick(tag.name);
            }}>
              {tag.name}
            </span>
          ))}
        </div>
      </div>
      
      <div className="sample-card-footer">
        <div className="sample-stats">
          <span className="stat">
            <FaEye /> {sample.views || 0} views
          </span>
          <span className="stat">
            <FaDownload /> {sample.downloads || 0} downloads
          </span>
          {sample.grade_received && (
            <span className="grade">
              Grade: {sample.grade_received}
            </span>
          )}
        </div>
        
        <div className="sample-actions">
          <button 
            className="btn-preview"
            onClick={(e) => handlePreviewClick(sample, e)}
            title="Preview"
          >
            <FaEye />
          </button>
          <button 
            className="btn-download"
            onClick={(e) => handleDownload(sample, e)}
            title="Download"
          >
            <FaDownload />
          </button>
        </div>
      </div>
    </div>
  );

  const renderSampleList = (sample) => (
    <div key={sample.id} className="sample-list-item" onClick={() => handleSampleClick(sample)}>
      <div className="list-item-left">
        {sample.thumbnail_url ? (
          <img src={sample.thumbnail_url} alt={sample.title} className="list-thumbnail" />
        ) : (
          <div className="list-thumbnail-placeholder">
            <FaFilePdf />
          </div>
        )}
      </div>
      
      <div className="list-item-center">
        <h3 className="list-title">{sample.title}</h3>
        <p className="list-excerpt">{sample.excerpt || sample.abstract?.substring(0, 200) + '...'}</p>
        
        <div className="list-meta">
          <span className="meta-item">
            <FaGraduationCap /> {sample.academic_level?.replace('_', ' ')}
          </span>
          <span className="meta-item">
            <FaBook /> {sample.subject}
          </span>
          <span className="meta-item">
            <FaTag /> {sample.paper_type?.replace('_', ' ')}
          </span>
          {sample.word_count && (
            <span className="meta-item">
              <FaClock /> {getReadingTime(sample.word_count)}
            </span>
          )}
        </div>
        
        <div className="list-tags">
          {sample.tags?.slice(0, 4).map(tag => (
            <span 
              key={tag.id} 
              className="list-tag"
              onClick={(e) => {
                e.stopPropagation();
                handleCategoryClick(tag.name);
              }}
            >
              {tag.name}
            </span>
          ))}
        </div>
      </div>
      
      <div className="list-item-right">
        <div className="list-stats">
          <span className="stat">
            <FaEye /> {sample.views || 0}
          </span>
          <span className="stat">
            <FaDownload /> {sample.downloads || 0}
          </span>
        </div>
        
        <div className="list-actions">
          <button 
            className="btn-preview"
            onClick={(e) => handlePreviewClick(sample, e)}
            title="Preview"
          >
            <FaEye /> Preview
          </button>
          <button 
            className="btn-download"
            onClick={(e) => handleDownload(sample, e)}
            title="Download"
          >
            <FaDownload /> Download
          </button>
        </div>
        
        <div className="list-date">
          {formatDate(sample.published_at || sample.created_at)}
        </div>
      </div>
    </div>
  );

  return (
    <div className="samples-page">
      {/* Hero Section */}
      <div className="samples-hero">
        <div className="hero-container">
          <h1 className="hero-title">
            <span className="hero-title-main">Essay Samples by Category</span>
            <span className="hero-title-sub">
              You may be sure that there are lots of options for college paper subjects when it comes to essay writing. 
              It seems that professors, instructors, and teachers are nothing more than inventive machines capable 
              of coming up with an unlimited list of essay topics for universities, colleges, and schools.
            </span>
          </h1>
          
          <div className="hero-search">
            <div className="search-container">
              <FaSearch className="search-icon" />
              <input
                ref={searchInputRef}
                type="text"
                placeholder="Tell us about the writing problem you're stuck on..."
                className="search-input"
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && applyFilters()}
              />
              <button className="search-btn" onClick={applyFilters}>
                Find Essay
              </button>
            </div>
            <p className="search-hint">
              Search by topic, subject, or keywords to find relevant samples
            </p>
          </div>
        </div>
      </div>

      {/* Featured Categories */}
      <div className="categories-section">
        <div className="section-header">
          <h2 className="section-title">Popular Essay Topics</h2>
          <p className="section-subtitle">Browse samples by popular categories</p>
        </div>
        
        <div className="categories-grid">
          {POPULAR_CATEGORIES.map((category, index) => (
            <button
              key={category}
              className={`category-chip ${selectedCategory === category ? 'active' : ''}`}
              onClick={() => handleCategoryClick(category)}
            >
              {category}
              <span className="category-count">{index % 10 + 1}</span>
            </button>
          ))}
        </div>
        
        <div className="categories-actions">
          <button className="btn-show-more">
            Show More Topics <FaChevronRight />
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="samples-main">
        <div className="container">
          {/* Sidebar Filters */}
          <div className="samples-sidebar">
            <div className="sidebar-section">
              <h3 className="sidebar-title">
                <FaFilter /> Filters
                {selectedCategory || selectedPaperType || selectedAcademicLevel || selectedFormat || searchQuery ? (
                  <button className="clear-filters-btn" onClick={clearFilters}>
                    <FaTimes /> Clear All
                  </button>
                ) : null}
              </h3>
              
              <div className="filter-group">
                <label className="filter-label">Paper Type</label>
                <div className="filter-options">
                  {PAPER_TYPES.map(type => (
                    <button
                      key={type}
                      className={`filter-option ${selectedPaperType === type ? 'active' : ''}`}
                      onClick={() => setSelectedPaperType(type === selectedPaperType ? '' : type)}
                    >
                      {type}
                    </button>
                  ))}
                </div>
              </div>
              
              <div className="filter-group">
                <label className="filter-label">Academic Level</label>
                <div className="filter-options">
                  {ACADEMIC_LEVELS.map(level => (
                    <button
                      key={level}
                      className={`filter-option ${selectedAcademicLevel === level ? 'active' : ''}`}
                      onClick={() => setSelectedAcademicLevel(level === selectedAcademicLevel ? '' : level)}
                    >
                      {level}
                    </button>
                  ))}
                </div>
              </div>
              
              <div className="filter-group">
                <label className="filter-label">Format Style</label>
                <div className="filter-options">
                  {FORMAT_STYLES.map(format => (
                    <button
                      key={format}
                      className={`filter-option ${selectedFormat === format ? 'active' : ''}`}
                      onClick={() => setSelectedFormat(format === selectedFormat ? '' : format)}
                    >
                      {format}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            
            {/* Featured Samples */}
            <div className="sidebar-section">
              <h3 className="sidebar-title">
                <FaStar /> Featured Samples
              </h3>
              <div className="featured-samples-sidebar">
                {featuredSamples.slice(0, 3).map(sample => (
                  <div key={sample.id} className="featured-sidebar-item" onClick={() => handleSampleClick(sample)}>
                    <h4 className="featured-title">{sample.title}</h4>
                    <p className="featured-subject">{sample.subject}</p>
                    <div className="featured-meta">
                      <span>{sample.academic_level?.replace('_', ' ')}</span>
                      <span>{sample.format_style}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Main Content Area */}
          <div className="samples-content">
            {/* Results Header */}
            <div className="results-header">
              <div className="results-info">
                <h2 className="results-title">
                  {selectedCategory ? `${selectedCategory} Samples` : 'All Essay Samples'}
                </h2>
                <p className="results-count">
                  {filteredSamples.length} {filteredSamples.length === 1 ? 'sample' : 'samples'} found
                  {searchQuery && ` for "${searchQuery}"`}
                </p>
              </div>
              
              <div className="results-controls">
                <div className="view-controls">
                  <button 
                    className={`view-btn ${viewMode === 'grid' ? 'active' : ''}`}
                    onClick={() => setViewMode('grid')}
                    title="Grid View"
                  >
                    <FaTh />
                  </button>
                  <button 
                    className={`view-btn ${viewMode === 'list' ? 'active' : ''}`}
                    onClick={() => setViewMode('list')}
                    title="List View"
                  >
                    <FaList />
                  </button>
                </div>
                
                <div className="sort-controls">
                  <select 
                    className="sort-select"
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                  >
                    <option value="newest">Newest First</option>
                    <option value="oldest">Oldest First</option>
                    <option value="popular">Most Popular</option>
                    <option value="alphabetical">A to Z</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Results Grid/List */}
            {loading ? (
              <div className="loading-state">
                <div className="spinner"></div>
                <p>Loading samples...</p>
              </div>
            ) : error ? (
              <div className="error-state">
                <p className="error-message">{error}</p>
                <button className="retry-btn" onClick={fetchSamples}>
                  Try Again
                </button>
              </div>
            ) : filteredSamples.length === 0 ? (
              <div className="no-results">
                <FaSearch className="no-results-icon" />
                <h3>No samples found</h3>
                <p>Try adjusting your search or filters</p>
                <button className="clear-filters-btn" onClick={clearFilters}>
                  Clear All Filters
                </button>
              </div>
            ) : (
              <>
                <div className={`samples-results ${viewMode}`}>
                  {viewMode === 'grid' 
                    ? currentSamples.map(renderSampleCard)
                    : currentSamples.map(renderSampleList)
                  }
                </div>
                
                {/* Pagination */}
                {renderPagination()}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Preview Modal */}
      {showPreviewModal && selectedSample && (
        <div className="preview-modal">
          <div className="preview-modal-content">
            <div className="preview-modal-header">
              <h3>Sample Preview: {selectedSample.title}</h3>
              <button 
                className="close-modal"
                onClick={() => setShowPreviewModal(false)}
              >
                <FaTimes />
              </button>
            </div>
            <div className="preview-modal-body">
              <div className="preview-info">
                <div className="preview-meta">
                  <span><FaGraduationCap /> {selectedSample.academic_level?.replace('_', ' ')}</span>
                  <span><FaBook /> {selectedSample.subject}</span>
                  <span><FaUserGraduate /> {selectedSample.paper_type?.replace('_', ' ')}</span>
                  <span><FaClock /> {getReadingTime(selectedSample.word_count)}</span>
                </div>
                
                <h4>Abstract</h4>
                <p className="preview-abstract">{selectedSample.abstract}</p>
                
                <h4>Key Features</h4>
                {selectedSample.key_features ? (
                  <ul className="preview-features">
                    {selectedSample.key_features.split('\n').map((feature, index) => (
                      <li key={index}>{feature}</li>
                    ))}
                  </ul>
                ) : (
                  <p>No key features listed</p>
                )}
              </div>
              
              <div className="preview-actions">
                <button 
                  className="btn-preview-download"
                  onClick={(e) => handleDownload(selectedSample, e)}
                >
                  <FaDownload /> Download Full Sample
                </button>
                <button 
                  className="btn-view-full"
                  onClick={() => {
                    setShowPreviewModal(false);
                    handleSampleClick(selectedSample);
                  }}
                >
                  <FaExternalLinkAlt /> View Full Details
                </button>
              </div>
            </div>
          </div>
          <div className="preview-modal-overlay" onClick={() => setShowPreviewModal(false)}></div>
        </div>
      )}

      {/* Footer */}
      <div className="samples-footer">
        <div className="footer-content">
          <div className="footer-section">
            <h4>Need Help With Your Paper?</h4>
            <p>Our professional writers can help you with any type of academic writing</p>
            <Link to="/order" className="footer-order-btn">
              Order Custom Paper
            </Link>
          </div>
          
          <div className="footer-section">
            <h4>Contact Support</h4>
            <p className="footer-contact">
              <strong>Email:</strong> researchwriting.infor@gmail.com<br />
              <strong>Phone:</strong> +254-718-583-950
            </p>
          </div>
          
          <div className="footer-section">
            <h4>We Accept</h4>
            <div className="payment-methods">
              <span className="payment-method">Visa</span>
              <span className="payment-method">MasterCard</span>
              <span className="payment-method">PayPal</span>
              <span className="payment-method">American Express</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SamplesPage;
